import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import type { BackupTriggerType, BackupStatus } from "../components/enum/Backup";

const API_URL = `${process.env.REACT_APP_API_URL}/backup`;
const BACKUP_LOGS_QUERY_KEY = ["backup-execution-logs"] as const;

export interface BackupExecutionLog {
  backupExecutionLogId: number;
  triggerType: BackupTriggerType;
  backupStartTime: string;
  backupEndTime: string | null;
  status: BackupStatus;
  initiatedBy: string | null;
  errorStackTrace: string | null;
  errorMessage: string | null;
  requestDate: string;
  requestUuid: string;
  backupFilePath: string | null;
}

// Initiate a manual backup
export const initiateBackup = async (): Promise<string> => {
  try {
    const response = await axios.post(API_URL);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getAllBackup = async (): Promise<BackupExecutionLog[]> => {
  try {
    const response =await axios.get(API_URL);
    return response.data;
  }catch(error){
    throw error;
  }
}

const extractFilenameFromContentDisposition = (
  contentDisposition: string | undefined
): string | null => {
  if (!contentDisposition) return null;
  // e.g. attachment; filename="backup.zip"
  const match = /filename\*?=(?:UTF-8''|")?([^;"\n]+)"?/i.exec(contentDisposition);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

export const downloadBackup = async (requestId: string): Promise<void> => {
  // Must use an authenticated request (Authorization header) to avoid 403.
  // Then we trigger a browser download using a blob URL.
  const token = localStorage.getItem("token") || sessionStorage.getItem("token");
  const response = await axios.get(`${API_URL}/download/${requestId}`, {
    responseType: "blob",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });

  const filename =
    extractFilenameFromContentDisposition(response.headers?.["content-disposition"]) ??
    `backup_${requestId}.zip`;

  const blob = new Blob([response.data], { type: "application/zip" });
  const url = window.URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();

  setTimeout(() => {
    link.remove();
    window.URL.revokeObjectURL(url);
  }, 100);
};

export const deleteBackup = async (requestId: string): Promise<void> => {
  await axios.delete(`${API_URL}/${requestId}`);
};

export function useBackup() {
  const queryClient = useQueryClient();

  const initiateBackupMutation = useMutation({
    mutationFn: initiateBackup,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: BACKUP_LOGS_QUERY_KEY });
    },
  });

  const downloadBackupMutation = useMutation({
    mutationFn: downloadBackup,
  });

  const deleteBackupMutation = useMutation({
    mutationFn: deleteBackup,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: BACKUP_LOGS_QUERY_KEY });
    },
  });

  const backupExecutionLogsQuery = useQuery({
    queryKey: BACKUP_LOGS_QUERY_KEY,
    queryFn: getAllBackup,
  });

  return {
    initiateBackupMutation,
    downloadBackupMutation,
    deleteBackupMutation,
    backupExecutionLogsQuery,
  };
}
